<script setup lang="ts">
    import { mdiCodeJson, mdiDownload, mdiFilterMultiple, mdiFormatText, mdiFullscreen, mdiFullscreenExit, mdiRefresh } from '@mdi/js';
    import { useDisplay } from 'vuetify';
    import AppExpandableSearch from '@/components/search/AppExpandableSearch.vue';
    import { useJsonViewModal } from '@/composables/useJsonViewModal';
    import { usePageExportModal } from '@/composables/usePageExportModal';
    import { usePageFiltersModal } from '@/composables/usePageFiltersModal';
    import { useWindowCopyModal } from '@/composables/useWindowCopyModal';
    import { useWindowReloadModal } from '@/composables/useWindowReloadModal';

    const { mobile } = useDisplay();
    const { t } = useI18n();

    const {
        title,
        icon = null,
        showFullscreenButton = false,
        isFullscreen = false,
        onFullscreenToggle,
        windowId,
        showFiltersButton = false,
        showJsonToggleButton = false,
        showSearch = true,
        showMinimizeButton = false,
    } = defineProps<{
        title: string;
        icon?: string | null;
        showFullscreenButton?: boolean;
        isFullscreen?: boolean;
        onFullscreenToggle?: () => void;
        windowId?: string;
        showFiltersButton?: boolean;
        showJsonToggleButton?: boolean;
        showSearch?: boolean;
        showMinimizeButton?: boolean;
    }>();
    const emit = defineEmits<{
        (e: 'close'): void;
        (e: 'minimize'): void;
    }>();

    const pageFiltersModal = usePageFiltersModal(windowId);
    const pageExportModal = usePageExportModal(windowId);
    const jsonViewModal = useJsonViewModal(windowId);
    const windowCopyModal = useWindowCopyModal(windowId);
    const windowReloadModal = useWindowReloadModal(windowId);

    const hasHiddenFilters = computed(() => {
        const modal = pageFiltersModal?.value;
        if (!modal) return false;
        return modal.hasHiddenFilters;
    });

    const hiddenFiltersCount = computed(() => {
        const modal = pageFiltersModal?.value;
        if (!modal) return 0;
        return modal.hiddenFiltersWithValuesCount;
    });

    const isJsonView = computed(() => {
        const modal = jsonViewModal?.value;
        if (!modal) return false;
        return modal.isJsonView;
    });

    const isExportLoading = computed(() => {
        const state = pageExportModal.value;
        if (!state) return false;
        return unref(state.loading);
    });

    const exportTooltip = computed(() => pageExportModal.value?.tooltip ?? t('action.export'));

    const isCopyDisabled = computed(() => {
        const state = windowCopyModal.value;
        if (!state) return true;
        if (state.disabled === undefined) return false;
        return unref(state.disabled);
    });

    const copyTooltip = computed(() => windowCopyModal.value?.tooltip ?? t('action.copy'));

    const isReloadLoading = computed(() => {
        const state = windowReloadModal.value;
        if (!state) return false;
        if (state.loading === undefined) return false;
        return unref(state.loading);
    });

    const isReloadDisabled = computed(() => {
        const state = windowReloadModal.value;
        if (!state) return true;
        if (state.disabled === undefined) return false;
        return unref(state.disabled);
    });

    const reloadTooltip = computed(() => windowReloadModal.value?.tooltip ?? t('action.reload'));

    const openFiltersDialog = () => {
        if (pageFiltersModal?.value) {
            pageFiltersModal.value.openModal();
        }
    };

    const close = () => {
        emit('close');
    };

    const minimize = () => {
        emit('minimize');
    };

    const handleFullscreenToggle = () => {
        if (onFullscreenToggle) {
            onFullscreenToggle();
        }
    };

    const handleJsonToggle = () => {
        if (jsonViewModal?.value) {
            jsonViewModal.value.toggleJsonView();
        }
    };

    const handleExport = () => {
        void pageExportModal.value?.onExport();
    };

    const handleCopy = () => {
        void windowCopyModal.value?.onCopy();
    };

    const handleReload = () => {
        void windowReloadModal.value?.onReload();
    };
</script>

<template>
    <v-card-title class="d-flex align-center mx-3 mb-0 px-0" style="border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity))">
        <v-btn
            v-if="showFullscreenButton && !mobile"
            :icon="isFullscreen ? mdiFullscreenExit : mdiFullscreen"
            @click="handleFullscreenToggle"
            variant="text"
            size="default"
            density="compact"
            style="flex-shrink: 0; margin-right: 8px"
        />

        <v-icon v-if="icon" :icon="icon" class="me-2" />

        <span class="text-h6 flex-grow-1 text-truncate">
            {{ title }}
        </span>

        <v-spacer />

        <AppExpandableSearch v-if="mobile && showSearch" class="mr-1" />

        <v-badge
            v-if="showFiltersButton && hasHiddenFilters"
            :content="hiddenFiltersCount"
            :model-value="hiddenFiltersCount > 0"
            color="orange"
            floating
            offset-x="4"
            offset-y="6"
            class="mr-2 filters-badge"
            @click="openFiltersDialog"
        >
            <v-btn :icon="mdiFilterMultiple" variant="text" size="default" density="compact" color="tbGreen" />
        </v-badge>

        <v-btn
            v-if="showJsonToggleButton"
            :icon="isJsonView ? mdiFormatText : mdiCodeJson"
            @click="handleJsonToggle"
            variant="text"
            size="default"
            density="compact"
            class="mr-2"
            color="tbGreen"
            :data-tooltip="isJsonView ? t('bets.log.showFormatted') : t('bets.log.showJson')"
        />

        <v-btn
            v-if="windowCopyModal"
            :icon="mdiContentCopy"
            variant="text"
            size="default"
            density="compact"
            class="mr-2"
            color="tbGreen"
            :disabled="isCopyDisabled"
            :data-tooltip="copyTooltip"
            @click="handleCopy"
        />

        <v-btn
            v-if="pageExportModal"
            :icon="mdiDownload"
            variant="text"
            size="default"
            density="compact"
            class="mr-2"
            color="tbGreen"
            :loading="isExportLoading"
            :disabled="isExportLoading"
            :data-tooltip="exportTooltip"
            @click="handleExport"
        />

        <v-btn
            v-if="windowReloadModal"
            :icon="mdiRefresh"
            variant="text"
            size="default"
            density="compact"
            class="mr-2"
            color="tbGreen"
            :loading="isReloadLoading"
            :disabled="isReloadDisabled || isReloadLoading"
            :data-tooltip="reloadTooltip"
            @click="handleReload"
        />

        <v-btn
            v-if="showMinimizeButton"
            :icon="mdiWindowMinimize"
            variant="text"
            size="default"
            density="compact"
            class="mr-2"
            :data-tooltip="t('window.minimize')"
            @click.stop="minimize"
        />

        <v-btn :icon="mdiClose" @click="close" variant="text" />
    </v-card-title>
</template>
